import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";

// @mui components
import AppBar from "@mui/material/AppBar";
import Badge from "@mui/material/Badge";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { Tabs, Tab, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import LanguageSwitcher from "../../../components/LanguageSwitcher";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import useIsMobile from "hooks/useIsMobile";

// Custom styles
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

import { useUserContext } from "context/UserContext";
import NotificationItem from "examples/Items/NotificationItem";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { isBkLikeRole } from "utils/roleUtils";

const renderMenu = () => (
  <Menu
    anchorEl={openMenu}
    anchorReference={null}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "left",
    }}
    open={Boolean(openMenu)}
    onClose={handleCloseMenu}
    sx={{ mt: 2 }}
  >
    <NotificationItem icon={<Icon>email</Icon>} title="Check new messages" />
    <NotificationItem icon={<Icon>podcasts</Icon>} title="Manage Podcast sessions" />
    <NotificationItem icon={<Icon>shopping_cart</Icon>} title="Payment successfully completed" />
  </Menu>
);

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [openMenu, setOpenMenu] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [inputMode, setInputMode] = useState("csv"); // csv | manual

  const [form, setForm] = useState({
    code: "",
    barcode: "",
    name: "",
    specs: "",
    venderCode: "",
    venderName: "",
    brand: "",
    movieOrPortrait: "",
    nature: "",
    productSeries: "",
    categoryName: "",
    price: "",
    description: "",
    pVideos: "",
    costPrice: "",
    isEnable: "1",
    packagingSize: "",
    capsuleSize: "",
    validUntil: "",
    mallListing: "N",
    prMethod: "",
    wAttributes: "",
    pBrand: "",
    pAttributes: "",
    tagList: "",
    notePublic: "",
    displayName: "",
  });

  const { userData, role } = useUserContext() || {};
  const route = useLocation().pathname.split("/").slice(1);
  const { t } = useTranslation("common");

  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const isMobile = useIsMobile();

  useEffect(() => {
    if (role && role !== "SP" && i18n.language !== "zh-TW") {
      i18n.changeLanguage("zh-TW");
    }
  }, [role, i18n]);

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    const handleTransparentNavbar = () => {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    };

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();

    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);

  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCsvFile(null);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const getCharCount = (str: string) => {
    let count = 0;
    for (let char of str) {
      // 中文
      if (char.charCodeAt(0) > 127) {
        count += 2;
      } else {
        // 英文或其他 ASCII
        count += 1;
      }
    }
    return count;
  };

  const handleChangeWithLimit = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (getCharCount(value) <= 30) {
      handleChange(e);
    }
  };

  const handleSubmit = async () => {
    const formData = new FormData();

    if (inputMode === "csv" && csvFile) {
      formData.append("file", csvFile);
    } else {
      // 手動填寫 → 轉成 CSV
      const csvContent = [
        [
          "ProductCode",
          "Barcode",
          "ProductName",
          "ProductSpecs",
          "VenderCode",
          "VenderName",
          "Brand",
          "MovieOrPortrait",
          "Nature",
          "ProductSeries",
          "CategoryName",
          "Price",
          "Description",
          "PVideos",
          "CostPrice",
          "IsEnable",
          "PackagingSize",
          "CapsuleSize",
          "ValidUntil",
          "MallListing",
          "PrMethod",
          "WAttributes",
          "PBrand",
          "PAttributes",
          "TagList",
          "NotePublic",
          "DisplayName",
        ],
        [
          form.code,
          form.barcode,
          form.name,
          form.specs,
          form.venderCode,
          form.venderName,
          form.brand,
          form.movieOrPortrait,
          form.nature,
          form.productSeries,
          form.categoryName,
          form.price,
          form.description,
          form.pVideos,
          form.costPrice,
          form.isEnable,
          form.packagingSize,
          form.capsuleSize,
          form.validUntil,
          form.mallListing,
          form.prMethod,
          form.wAttributes,
          form.pBrand,
          form.pAttributes,
          form.tagList,
          form.notePublic,
          form.displayName,
        ],
      ]
        .map((row) => row.join(","))
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv" });
      formData.append("file", blob, "product.csv");
    }

    try {
      const response = await fetch(
        "https://crm-mqtt-718408119422.asia-east1.run.app/api/v1/product/create",
        {
          method: "POST",
          headers: {
            "Mdm-Auth": "57b2a9ba-4428-48e4-b137-f8337b68595e",
          },
          body: formData,
        }
      );

      const result = await response.json();
      console.log(result);

      if (result?.data?.failed_details && result.data.failed_details.length > 0) {
        const failedMessages = result.data.failed_details
          .map((item) => `${item.product_code}: ${item.reason}`)
          .join("\n");
        alert(`上傳部分失敗：\n${failedMessages}`);
      } else {
        alert("上傳成功！");
      }
    } catch (error) {
      alert("上傳失敗：" + error.message);
    }

    // reset
    setForm({
      code: "",
      barcode: "",
      name: "",
      specs: "",
      venderCode: "",
      venderName: "",
      brand: "",
      movieOrPortrait: "",
      nature: "",
      productSeries: "",
      categoryName: "",
      price: "",
      description: "",
      pVideos: "",
      costPrice: "",
      isEnable: "1",
      packagingSize: "",
      capsuleSize: "",
      validUntil: "",
      mallListing: "N",
      prMethod: "",
      wAttributes: "",
      pBrand: "",
      pAttributes: "",
      tagList: "",
      notePublic: "",
      displayName: "",
    });
    setCsvFile(null);
    setOpenDialog(false);
  };

  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;
      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }
      return colorValue;
    },
  });

  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem icon={<Icon>email</Icon>} title="Check new messages" />
      <NotificationItem icon={<Icon>podcasts</Icon>} title="Manage Podcast sessions" />
      <NotificationItem icon={<Icon>shopping_cart</Icon>} title="Payment successfully completed" />
    </Menu>
  );

  return (
    <>
      <AppBar
        position={absolute ? "absolute" : navbarType}
        color="inherit"
        sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
      >
        <Toolbar
          sx={(theme) => ({
            ...navbarContainer(theme),
            width: "100%",
            flexWrap: isMobile ? "wrap" : "nowrap",
            rowGap: isMobile ? theme.spacing(1.5) : 0,
            columnGap: theme.spacing(2),
            px: isMobile ? theme.spacing(1.5) : theme.spacing(2),
            py: isMobile ? theme.spacing(1) : theme.spacing(0.5),
          })}
        >
          {!isMini && (
            <MDBox
              sx={(theme) => ({
                ...navbarRow(theme, { isMini }),
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "stretch" : "center",
                width: "100%",
                gap: isMobile ? theme.spacing(1) : 0,
              })}
            >
              <MDBox
                display="flex"
                alignItems={isMobile ? "flex-start" : "center"}
                flexDirection={isMobile ? "column" : "row"}
                pr={isMobile ? 0 : 2}
                color={light ? "white" : "inherit"}
                width={isMobile ? "100%" : "auto"}
                gap={isMobile ? 0.5 : 0}
              >
                <IconButton
                  size="large"
                  disableRipple
                  color="inherit"
                  sx={{ width: "40px", height: "40px", fontSize: "40px", mr: 2 }}
                  onClick={handleMiniSidenav}
                >
                  <Icon fontSize="inherit">{miniSidenav ? "menu_open" : "menu"}</Icon>
                </IconButton>
                <MDTypography
                  variant="button"
                  fontWeight="medium"
                  color={light ? "white" : "text"}
                  sx={{
                    mr: isMobile ? 0 : 2,
                    mb: isMobile ? 0.25 : 0,
                    display: "block",
                  }}
                >
                  {role === "SP"
                    ? t("welcome.sp")
                    : role === "MN"
                    ? t("welcome.mn")
                    : t("welcome.bk")}{" "}
                  {userData?.business_name ?? "User"}
                </MDTypography>
                <MDTypography
                  variant="caption"
                  color={light ? "white" : "text"}
                  sx={{ opacity: 0.8, wordBreak: "break-word" }}
                >
                  {userData?.email ?? ""}
                </MDTypography>
              </MDBox>

              {isBkLikeRole(role) && (
                <MDBox
                  ml={isMobile ? 0 : "auto"}
                  width={isMobile ? "100%" : "auto"}
                  display="flex"
                  justifyContent={isMobile ? "flex-start" : "flex-end"}
                >
                  <Button
                    variant="contained"
                    onClick={handleOpenDialog}
                    fullWidth={isMobile}
                    sx={{
                      backgroundColor: "#FF9800",
                      color: "#FFFFFF",
                      "&:hover": { backgroundColor: "#FB8C00" },
                    }}
                  >
                    {t("actions.createProduct")}
                  </Button>
                </MDBox>
              )}
            </MDBox>
          )}
          {role === "SP" && (
            <MDBox ml="auto" display="flex" alignItems="center">
              <LanguageSwitcher iconColor={light ? "white" : "inherit"} />
            </MDBox>
          )}
          {/* <Badge
            color="error"
            variant="dot"
            invisible={true}
            overlap="circular" // 使用圓形圖標
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <IconButton
              size="small"
              disableRipple
              color="inherit"
              sx={navbarIconButton}
              aria-controls="notification-menu"
              aria-haspopup="true"
              variant="contained"
              onClick={handleOpenMenu}
            >
              <Icon sx={iconsStyle}>notifications</Icon>
            </IconButton>
            {renderMenu()}
          </Badge> */}
        </Toolbar>
      </AppBar>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>創建新商品</DialogTitle>
        <DialogContent>
          <Tabs value={inputMode} onChange={(e, v) => setInputMode(v)} sx={{ mb: 2 }}>
            <Tab label="上傳 CSV" value="csv" />
            <Tab label="手動輸入" value="manual" />
          </Tabs>

          {inputMode === "csv" && (
            <Box mt={2}>
              <Typography variant="subtitle2">上傳產品 CSV：</Typography>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                style={{ marginTop: 8 }}
              />
              {csvFile && (
                <Typography variant="body2" color="primary">
                  已選擇：{csvFile.name}
                </Typography>
              )}
            </Box>
          )}

          {inputMode === "manual" && (
            <Grid container spacing={2}>
              {/* 必填 */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="商品代號 *"
                  name="code"
                  value={form.code}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="國際條碼 *"
                  name="barcode"
                  value={form.barcode}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="商品名稱 *"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="商品顯示名稱 * (最多30字元)"
                  name="displayName"
                  value={form.displayName}
                  onChange={handleChangeWithLimit}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="售價 *"
                  name="price"
                  type="number"
                  value={form.price}
                  onChange={handleChange}
                  required
                />
              </Grid>

              {/* 選填 */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="商品規格"
                  name="specs"
                  value={form.specs}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="廠商代號"
                  name="venderCode"
                  value={form.venderCode}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="廠商名稱"
                  name="venderName"
                  value={form.venderName}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="品牌"
                  name="brand"
                  value={form.brand}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="電影或肖像"
                  name="movieOrPortrait"
                  value={form.movieOrPortrait}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="商品性質"
                  name="nature"
                  value={form.nature}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="商品系列"
                  name="productSeries"
                  value={form.productSeries}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="商品類別名稱"
                  name="categoryName"
                  value={form.categoryName}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="商品描述"
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="商品影片連結"
                  name="pVideos"
                  value={form.pVideos}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="成本"
                  name="costPrice"
                  value={form.costPrice}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  {/* <InputLabel id="isEnable-label">是否啟用</InputLabel> */}
                  <Select
                    labelId="isEnable-label"
                    id="isEnable"
                    name="isEnable"
                    value={form.isEnable}
                    onChange={handleChange}
                    sx={{ minHeight: 42 }}
                  >
                    <MenuItem value="1">啟用</MenuItem>
                    <MenuItem value="0">停用</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="包裝尺寸"
                  name="packagingSize"
                  value={form.packagingSize}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="扭蛋殼尺寸"
                  name="capsuleSize"
                  value={form.capsuleSize}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="保存期限"
                  name="validUntil"
                  value={form.validUntil}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  {/* <InputLabel>是否商城上架</InputLabel> */}
                  <Select
                    name="mallListing"
                    value={form.mallListing}
                    onChange={handleChange}
                    sx={{ minHeight: 42 }}
                  >
                    <MenuItem value="N">商城上架 - 否</MenuItem>
                    <MenuItem value="Y">商城上架 - 是</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="採購方式"
                  name="prMethod"
                  value={form.prMethod}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="作品屬性"
                  name="wAttributes"
                  value={form.wAttributes}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="產品品牌"
                  name="pBrand"
                  value={form.pBrand}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="商品屬性"
                  name="pAttributes"
                  value={form.pAttributes}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="商品標籤 (以,分隔)"
                  name="tagList"
                  value={form.tagList}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="公開備註"
                  name="notePublic"
                  value={form.notePublic}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>取消</Button>
          <Button
            onClick={handleSubmit}
            sx={{ mt: 2, mb: 2, color: "white !important" }}
            variant="contained"
            disabled={
              (inputMode === "csv" && !csvFile) ||
              (inputMode === "manual" &&
                (!form.code?.trim() ||
                  !form.barcode?.trim() ||
                  !form.name?.trim() ||
                  !form.price ||
                  !form.displayName?.trim()))
            }
          >
            送出
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
